// INDEX_SPLIT_B3_v1 (07.09.2026) -- KI-Empfang + Nachrichten: AICallsPage, MessagesPage, RetellSetupCard
// 1:1 aus app.jsx verschoben (Reihenfolge unverändert), nur `export` ergänzt und Imports erzeugt (scripts/split_frontend.mjs).
import { AlertCircle, Bell, Bot, Calendar, Check, CheckCircle, Eye, Globe, MessageSquare, Mic, MicOff, Phone, PhoneCall, PhoneForwarded, Send, Star, Zap, useEffect, useState } from "../core/vendor.js";
import { api } from "../core/api.js";
import { COLORS, isKosmetikSalon } from "../core/config.jsx";
import { aiCallTranscript } from "../core/demo-data.js";
import { Badge, Btn, Card, Input, Select, StatCard } from "../core/ui.jsx";
import { Header } from "../layout/layout.jsx";


// ============================================================
// PAGE: AI Calls (with Retell AI Integration)
// ============================================================
export const AICallsPage = () => {
  const [playing, setPlaying] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [activeTab, setActiveTab] = useState("live");
  const [setupStep, setSetupStep] = useState(0);
  const [phoneSetup, setPhoneSetup] = useState("redirect"); // redirect | retell | sip
  const [retellConnected, setRetellConnected] = useState(true);
  const [callLog] = useState([
    { id: 1, time: "09:15", caller: "+49 155 5678901", callerName: "Sophie Klein", duration: "1:42", result: "booked", lang: "DE", cost: "0.12", transcript: true },
    { id: 2, time: "10:30", caller: "+49 170 9876543", callerName: "Unbekannt", duration: "0:55", result: "escalated", lang: "DE", cost: "0.06", transcript: true },
    { id: 3, time: "11:45", caller: "+49 160 1122334", callerName: "Aylin Yilmaz", duration: "2:15", result: "booked", lang: "TR", cost: "0.16", transcript: true },
    { id: 4, time: "14:20", caller: "+49 151 9988776", callerName: "John Smith", duration: "1:08", result: "info", lang: "EN", cost: "0.08", transcript: true },
    { id: 5, time: "15:40", caller: "+49 162 4455667", callerName: "Maria Wagner", duration: "1:55", result: "booked", lang: "DE", cost: "0.14", transcript: true },
  ]);

  const [retellConfig, setRetellConfig] = useState({
    apiKey: "sk_ret_••••••••••••••••",
    agentId: "agent_salon_schoenheit_v2",
    retellNumber: "+49 30 9876 5432",
    salonNumber: "+49 30 1234 567",
    escalationNumber: "+49 170 1234567",
    maxDuration: 300,
    greeting: "Guten Tag! Willkommen bei Salon Schönheit. Mein Name ist Ava, Ihre digitale Assistentin. Wie kann ich Ihnen helfen?",
    languages: ["de", "en", "tr"],
    webhookUrl: "https://api.salon-schoenheit.de/retell/webhook",
    voiceId: "emma-german-v2",
  });

  useEffect(() => {
    if (playing && currentIdx < aiCallTranscript().length - 1) {
      const timer = setTimeout(() => setCurrentIdx(i => i + 1), 2000);
      return () => clearTimeout(timer);
    } else if (currentIdx >= aiCallTranscript().length - 1) {
      setPlaying(false);
    }
  }, [playing, currentIdx]);

  const startDemo = () => { setCurrentIdx(0); setPlaying(true); };

  const resultConfig = {
    booked: { label: "Termin gebucht", color: COLORS.success },
    escalated: { label: "Weiterleitung", color: COLORS.warning },
    info: { label: "Preisauskunft", color: COLORS.info },
    missed: { label: "Abgebrochen", color: COLORS.danger },
  };

  const bookedCount = callLog.filter(c => c.result === "booked").length; /* SMS_COST_HIDE_v1: todayCost entfernt */

  const tabs = [
    { id: "live", label: "Live-Demo", icon: Mic },
    { id: "calls", label: "Anrufprotokoll", icon: PhoneCall },
    { id: "setup", label: "Telefonie-Setup", icon: Phone },
    { id: "retell", label: "Retell AI Config", icon: Bot },
    { id: "flow", label: "Gesprächsfluss", icon: Zap },
  ];

  return (
    <div>
      <Header title="KI-Anrufannahme" subtitle="Retell AI Integration — Automatische Terminbuchung per Telefon" />

      {/* Stats */}
      {/* SMS_COST_HIDE_v1 -- KI-Kosten-Kachel entfernt (interne Plattformkosten) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <StatCard icon={PhoneCall} label="Anrufe heute" value={callLog.length} color={COLORS.primary} />
        <StatCard icon={CheckCircle} label="Gebucht" value={bookedCount} color={COLORS.success} />
        <StatCard icon={PhoneForwarded} label="Eskaliert" value={callLog.filter(c => c.result === "escalated").length} color={COLORS.warning} />
        <StatCard icon={Globe} label="Sprachen" value="3" color={COLORS.info} />
      </div>

      {/* Retell Connection Status */}
      <Card style={{ marginBottom: 16, padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", background: retellConnected ? `${COLORS.success}08` : `${COLORS.danger}08`, borderColor: retellConnected ? `${COLORS.success}30` : `${COLORS.danger}30` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: retellConnected ? COLORS.success : COLORS.danger, boxShadow: retellConnected ? `0 0 8px ${COLORS.success}80` : "none" }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            Retell AI: {retellConnected ? "Verbunden & aktiv" : "Nicht verbunden"}
          </span>
          <span style={{ fontSize: 12, color: COLORS.textLight }}>
            Agent: {retellConfig.agentId} | Stimme: {retellConfig.voiceId}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
          <span style={{ color: COLORS.textLight }}>Retell-Nr: <strong>{retellConfig.retellNumber}</strong></span>
          <span style={{ color: COLORS.textLight }}>→ Salon: <strong>{retellConfig.salonNumber}</strong></span>
          <Btn size="sm" variant={retellConnected ? "secondary" : "success"} onClick={() => setRetellConnected(!retellConnected)}>
            {retellConnected ? "Trennen" : "Verbinden"}
          </Btn>
        </div>
      </Card>

      {/* Tab Navigation */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: `2px solid ${COLORS.border}`, paddingBottom: 0 }}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", fontSize: 13, fontWeight: isActive ? 700 : 500,
              background: "none", border: "none", cursor: "pointer", color: isActive ? COLORS.primary : COLORS.textLight,
              borderBottom: isActive ? `2px solid ${COLORS.primary}` : "2px solid transparent", marginBottom: -2, transition: "all 0.15s"
            }}>
              <Icon size={15} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* TAB: Live Demo */}
      {activeTab === "live" && (
        <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 16 }}>
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Live-Demo: KI-Gespräch</h3>
              <Btn size="sm" onClick={startDemo} variant={playing ? "danger" : "primary"}>
                {playing ? <><MicOff size={14} /> Stop</> : <><Mic size={14} /> Demo starten</>}
              </Btn>
            </div>

            <div style={{ height: 360, overflow: "auto", background: "var(--cardHover)", borderRadius: 10, padding: 16 }}>
              {aiCallTranscript().slice(0, currentIdx + 1).map((msg, i) => (
                <div key={i} style={{ display: "flex", justifyContent: msg.role === "ai" ? "flex-start" : "flex-end", marginBottom: 10 }}>
                  <div style={{
                    maxWidth: "80%", padding: "10px 14px", borderRadius: 12,
                    background: msg.role === "ai" ? COLORS.primary : "var(--border)",
                    color: msg.role === "ai" ? "white" : COLORS.text, fontSize: 13, lineHeight: 1.5
                  }}>
                    <div style={{ fontSize: 10, opacity: 0.7, marginBottom: 4 }}>
                      {msg.role === "ai" ? "🤖 Ava (Retell AI)" : "📞 Anrufer"}
                    </div>
                    {msg.text}
                  </div>
                </div>
              ))}
              {playing && currentIdx < aiCallTranscript().length - 1 && (
                <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 10 }}>
                  <div style={{ padding: "10px 14px", borderRadius: 12, background: `${COLORS.primary}15`, fontSize: 13, color: COLORS.textLight }}>
                    ●●●
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: 12, padding: "10px 14px", background: `${COLORS.success}10`, borderRadius: 8, fontSize: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, color: COLORS.success }}>
                <Zap size={14} /> Retell AI → Webhook → Shearly → Buchung
              </div>
              <div style={{ marginTop: 6, color: COLORS.text }}>
                {isKosmetikSalon() ? "Service: Gesichtsbehandlung | Dauer: ~60 Min. | Preis: ~79 € | Mitarbeiterin: Kosmetikerin | Slot: Fr. 10:00" : "Service: Balayage | Dauer: ~3 Std. | Preis: ~150 € | Stylist: Tom Fischer | Slot: Fr. 10:00"}
              </div>
            </div>
          </Card>

          {/* Architecture Visualization */}
          <Card>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Anruffluss-Architektur</h3>
            <div style={{ background: "var(--cardHover)", borderRadius: 10, padding: 20 }}>
              {[
                { icon: Phone, label: "Kunde ruft Salonnummer an", sub: retellConfig.salonNumber, color: COLORS.text },
                { icon: PhoneForwarded, label: "Rufumleitung → Retell AI", sub: `Retell-Nr: ${retellConfig.retellNumber}`, color: COLORS.info },
                { icon: Bot, label: "Retell AI: STT + LLM + TTS", sub: "Sprache erkennen, Anliegen verstehen, antworten", color: COLORS.primary },
                { icon: Zap, label: "Webhook → Shearly Backend", sub: retellConfig.webhookUrl, color: COLORS.accent },
                { icon: Calendar, label: "Kalender-Check & Preiskalkulation", sub: "Verfügbarkeit prüfen, dynamischer Preis/Dauer", color: COLORS.success },
                { icon: CheckCircle, label: "Termin buchen + SMS-Bestätigung", sub: "Oder: Eskalation an Salontelefon", color: COLORS.success },
              ].map((step, i) => {
                const Icon = step.icon;
                return (
                  <div key={i}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: `${step.color}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon size={16} color={step.color} />
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{step.label}</div>
                        <div style={{ fontSize: 11, color: COLORS.textLight }}>{step.sub}</div>
                      </div>
                    </div>
                    {i < 5 && (
                      <div style={{ marginLeft: 18, borderLeft: `2px dashed ${COLORS.border}`, height: 16 }} />
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 16, padding: 12, background: `${COLORS.warning}10`, borderRadius: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: COLORS.warning }}>
                <AlertCircle size={13} style={{ verticalAlign: -2 }} /> Eskalation
              </div>
              <div style={{ color: COLORS.text }}>
                Wenn die KI nicht weiterkommt oder der Kunde "Mitarbeiter" sagt → Anruf wird per SIP-Transfer an <strong>{retellConfig.escalationNumber}</strong> weitergeleitet. Das Salontelefon klingelt erst dann.
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* TAB: Call Log */}
      {activeTab === "calls" && (
        <Card>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Anrufprotokoll — Heute</h3>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                {["Zeit", "Anrufer", "Name", "Dauer", "Sprache", "Ergebnis", ""].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.textLight, fontWeight: 600, fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {callLog.map(call => {
                const rc = resultConfig[call.result];
                return (
                  <tr key={call.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "10px 10px", fontWeight: 600 }}>{call.time}</td>
                    <td style={{ padding: "10px 10px", fontFamily: "monospace", fontSize: 12 }}>{call.caller}</td>
                    <td style={{ padding: "10px 10px" }}>{call.callerName}</td>
                    <td style={{ padding: "10px 10px" }}>{call.duration}</td>
                    <td style={{ padding: "10px 10px" }}><Badge text={call.lang} color={COLORS.info} /></td>
                    <td style={{ padding: "10px 10px" }}><Badge text={rc.label} color={rc.color} /></td>
                    <td style={{ padding: "10px 10px" }}>
                      {call.transcript && <Btn size="sm" variant="ghost"><Eye size={12} /> Transkript</Btn>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", padding: "12px 10px", background: "var(--cardHover)", borderRadius: 8, fontSize: 13 }}>
            <span><strong>Gesamt:</strong> {callLog.length} Anrufe</span>
            <span><strong>Gebucht:</strong> {bookedCount}/{callLog.length} ({Math.round(bookedCount / callLog.length * 100)}%)</span>
          </div>
        </Card>
      )}

      {/* TAB: Phone Setup Wizard */}
      {activeTab === "setup" && (
        <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 16 }}>
          <Card>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Telefonie-Setup</h3>
            <p style={{ fontSize: 13, color: COLORS.textLight, marginBottom: 16 }}>
              Wählen Sie, wie eingehende Anrufe an die KI weitergeleitet werden.
            </p>

            {[
              {
                id: "redirect", title: "Rufumleitung (empfohlen)",
                desc: "Salon behält bestehende Nummer. Einfache Rufumleitung zum Telefonanbieter einrichten → Anrufe gehen an Retell AI.",
                pros: ["Keine Nummernänderung", "In 5 Min. eingerichtet", "Kein eigener Server"],
                setup: "Beim Telefonanbieter: Rufumleitung aktivieren → Ziel: Retell-Nummer"
              },
              {
                id: "retell", title: "Retell-Nummer als Hauptnummer",
                desc: "Retell stellt eine virtuelle Telefonnummer bereit. Der Salon nutzt diese direkt.",
                pros: ["Einfachstes Setup", "Volle Retell-Kontrolle", "Sofort einsatzbereit"],
                setup: "Retell-Nummer auf Website, Google & Visitenkarten aktualisieren"
              },
              {
                id: "sip", title: "SIP-Trunk (Profi)",
                desc: "Telefonanlage routet eingehende Anrufe per SIP-Protokoll an Retell. Volle Kontrolle.",
                pros: ["Professionellste Lösung", "Mehrere Leitungen", "PBX-Integration"],
                setup: "SIP-Trunk-Provider konfigurieren → Retell SIP-Endpunkt als Ziel"
              },
            ].map(opt => (
              <div key={opt.id} onClick={() => setPhoneSetup(opt.id)} style={{
                padding: 16, borderRadius: 10, marginBottom: 10, cursor: "pointer", transition: "all 0.15s",
                border: `2px solid ${phoneSetup === opt.id ? COLORS.primary : COLORS.border}`,
                background: phoneSetup === opt.id ? `${COLORS.primary}06` : "var(--card)"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{opt.title}</h4>
                  {phoneSetup === opt.id && <Check size={18} color={COLORS.primary} />}
                </div>
                <p style={{ fontSize: 12, color: COLORS.textLight, margin: "6px 0" }}>{opt.desc}</p>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {opt.pros.map(p => (
                    <span key={p} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: `${COLORS.success}12`, color: COLORS.success }}>
                      ✓ {p}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </Card>

          <Card>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>
              Setup-Anleitung: {phoneSetup === "redirect" ? "Rufumleitung" : phoneSetup === "retell" ? "Retell-Nummer" : "SIP-Trunk"}
            </h3>

            {phoneSetup === "redirect" && (
              <div>
                <div style={{ fontSize: 13, lineHeight: 2.2 }}>
                  {[
                    { step: 1, title: "Retell AI Nummer erhalten", desc: `Ihre Retell-Nummer: ${retellConfig.retellNumber}`, done: true },
                    { step: 2, title: "Rufumleitung beim Anbieter einrichten", desc: `Telekom/Vodafone/O2: Rufumleitung für ${retellConfig.salonNumber} → ${retellConfig.retellNumber}`, done: true },
                    { step: 3, title: "Eskalationsnummer hinterlegen", desc: `Wenn KI eskaliert → Anruf geht an: ${retellConfig.escalationNumber}`, done: true },
                    { step: 4, title: "Testanruf durchführen", desc: "Rufen Sie Ihre Salonnummer an und testen Sie das KI-Gespräch", done: false },
                  ].map(s => (
                    <div key={s.step} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 14 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700,
                        background: s.done ? COLORS.success : COLORS.border, color: s.done ? "white" : COLORS.textLight
                      }}>
                        {s.done ? <Check size={14} /> : s.step}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{s.title}</div>
                        <div style={{ fontSize: 12, color: COLORS.textLight }}>{s.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 16, padding: 14, background: `${COLORS.info}08`, borderRadius: 10, fontSize: 12 }}>
                  <h4 style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700, color: COLORS.info }}>So funktioniert es:</h4>
                  <div style={{ lineHeight: 1.8, color: COLORS.text }}>
                    Kunde wählt <strong>{retellConfig.salonNumber}</strong> (Ihre normale Nummer) → Telefonanbieter leitet an <strong>{retellConfig.retellNumber}</strong> weiter → Retell AI nimmt an → Webhook an Shearly → Termin wird gebucht → Kunde bekommt SMS.
                    <br /><br />
                    Falls KI eskaliert: Retell leitet an <strong>{retellConfig.escalationNumber}</strong> weiter — erst dann klingelt das Salontelefon.
                  </div>
                </div>
              </div>
            )}

            {phoneSetup === "retell" && (
              <div style={{ fontSize: 13, lineHeight: 2 }}>
                <div style={{ padding: 14, background: `${COLORS.primary}08`, borderRadius: 10, marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Ihre Retell-Nummer:</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.primary }}>{retellConfig.retellNumber}</div>
                  <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 4 }}>Diese Nummer direkt als Geschäftsnummer verwenden</div>
                </div>
                <div><strong>1.</strong> Nummer auf Website aktualisieren</div>
                <div><strong>2.</strong> Google My Business Eintrag ändern</div>
                <div><strong>3.</strong> Visitenkarten / Flyer anpassen</div>
                <div><strong>4.</strong> Eskalationsnummer hinterlegen: <strong>{retellConfig.escalationNumber}</strong></div>
                <div><strong>5.</strong> Testanruf durchführen</div>
              </div>
            )}

            {phoneSetup === "sip" && (
              <div style={{ fontSize: 13, lineHeight: 2 }}>
                <Input label="SIP-Server" defaultValue="sip.salon-schoenheit.de" />
                <Input label="SIP-Benutzername" defaultValue="salon_main" />
                <Input label="SIP-Passwort" type="password" defaultValue="••••••••" />
                <Input label="Retell SIP-Endpunkt" defaultValue="sip:agent@retell.ai" />
                <div style={{ padding: 12, background: `${COLORS.warning}10`, borderRadius: 8, fontSize: 12, marginTop: 8 }}>
                  <AlertCircle size={13} color={COLORS.warning} style={{ verticalAlign: -2 }} /> Benötigt VoIP-fähige Telefonanlage (z.B. Fritz!Box, Asterisk, 3CX)
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* TAB: Retell Config */}
      {activeTab === "retell" && (
        <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 16 }}>
          <Card>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Retell AI Konfiguration</h3>
            <Input label="API Key" value={retellConfig.apiKey} onChange={e => setRetellConfig(c => ({ ...c, apiKey: e.target.value }))} />
            <Input label="Agent ID" value={retellConfig.agentId} onChange={e => setRetellConfig(c => ({ ...c, agentId: e.target.value }))} />
            <Select label="Stimme" value={retellConfig.voiceId} onChange={e => setRetellConfig(c => ({ ...c, voiceId: e.target.value }))} options={[
              { value: "emma-german-v2", label: "Emma (Deutsch, weiblich)" },
              { value: "hans-german-v1", label: "Hans (Deutsch, männlich)" },
              { value: "sofia-multilingual-v2", label: "Sofia (Multilingual, weiblich)" },
              { value: "alex-multilingual-v1", label: "Alex (Multilingual, männlich)" },
            ]} />
            <Input label="Begrüßungstext" value={retellConfig.greeting} onChange={e => setRetellConfig(c => ({ ...c, greeting: e.target.value }))} />
            <Input label="Max. Gesprächsdauer (Sek.)" type="number" value={retellConfig.maxDuration} onChange={e => setRetellConfig(c => ({ ...c, maxDuration: e.target.value }))} />

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Sprachen</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  { code: "de", label: "Deutsch" }, { code: "en", label: "English" },
                  { code: "tr", label: "Türkçe" }, { code: "ru", label: "Русский" },
                  { code: "ar", label: "العربية" }, { code: "fr", label: "Français" },
                  { code: "es", label: "Español" }, { code: "pl", label: "Polski" },
                ].map(lang => {
                  const active = retellConfig.languages.includes(lang.code);
                  return (
                    <button key={lang.code} onClick={() => setRetellConfig(c => ({
                      ...c, languages: active ? c.languages.filter(l => l !== lang.code) : [...c.languages, lang.code]
                    }))} style={{
                      padding: "6px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                      background: active ? `${COLORS.primary}15` : "#f3f4f6",
                      color: active ? COLORS.primary : COLORS.textLight,
                      border: `1px solid ${active ? COLORS.primary : COLORS.border}`,
                      fontWeight: active ? 600 : 400
                    }}>
                      {lang.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <Btn><Check size={14} /> Konfiguration speichern</Btn>
          </Card>

          <Card>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Webhook & API</h3>
            <Input label="Webhook-URL (Shearly Backend)" value={retellConfig.webhookUrl} onChange={e => setRetellConfig(c => ({ ...c, webhookUrl: e.target.value }))} />
            <Input label="Retell-Nummer" value={retellConfig.retellNumber} onChange={e => setRetellConfig(c => ({ ...c, retellNumber: e.target.value }))} />
            <Input label="Salon-Nummer (Anzeige)" value={retellConfig.salonNumber} onChange={e => setRetellConfig(c => ({ ...c, salonNumber: e.target.value }))} />
            <Input label="Eskalations-Nummer" value={retellConfig.escalationNumber} onChange={e => setRetellConfig(c => ({ ...c, escalationNumber: e.target.value }))} />

            <div style={{ marginTop: 16, padding: 14, background: "var(--cardHover)", borderRadius: 10, fontSize: 12 }}>
              <h4 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700 }}>Webhook Events</h4>
              <div style={{ lineHeight: 2 }}>
                {[
                  { event: "call.started", desc: "Anruf beginnt — Kundendaten laden" },
                  { event: "call.function_call", desc: "KI ruft Shearly API auf (Verfügbarkeit, Preise)" },
                  { event: "call.booking_request", desc: "KI möchte Termin buchen → Shearly bestätigt" },
                  { event: "call.escalation", desc: "KI eskaliert → Weiterleitung an Salontelefon" },
                  { event: "call.ended", desc: "Anruf beendet — Transkript speichern" },
                ].map(wh => (
                  <div key={wh.event} style={{ display: "flex", gap: 8 }}>
                    <code style={{ background: `${COLORS.primary}10`, padding: "1px 6px", borderRadius: 4, fontSize: 11, color: COLORS.primary }}>{wh.event}</code>
                    <span style={{ color: COLORS.textLight }}>{wh.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 16, padding: 14, background: `${COLORS.info}08`, borderRadius: 10, fontSize: 12 }}>
              <h4 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700, color: COLORS.info }}>Retell Function Calling</h4>
              <div style={{ color: COLORS.text, lineHeight: 1.8 }}>
                Die KI kann während des Gesprächs direkt Shearly-Funktionen aufrufen:
                <br /><br />
                <code style={{ display: "block", background: "#1F2937", color: "#10B981", padding: 10, borderRadius: 6, fontSize: 11, lineHeight: 1.6 }}>
                  {`// Retell ruft Shearly API auf:\n`}
                  {`check_availability(employee, date, duration)\n`}
                  {`get_dynamic_price(services[], hair_type, is_weekend)\n`}
                  {`create_booking(customer, employee, services[], slot)\n`}
                  {`send_sms_confirmation(customer_phone, booking_id)`}
                </code>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* TAB: Conversation Flow */}
      {activeTab === "flow" && (
        <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 16 }}>
          <Card>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Gesprächsfluss-Editor</h3>
            <p style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 16 }}>
              Definieren Sie die Logik, die Retell AI während eines Anrufs durchläuft.
            </p>
            {[
              { id: 1, title: "Begrüßung", desc: "Willkommen + Frage: Was möchten Sie?", color: COLORS.primary, active: true },
              { id: 2, title: "Service erkennen", desc: "NLP: Dienstleistung aus Sprache extrahieren", color: COLORS.info, active: true },
              { id: 3, title: "Haardetails erfragen", desc: "Länge, Dicke, besondere Wünsche", color: COLORS.info, active: true },
              { id: 4, title: "Preis & Dauer kalkulieren", desc: "→ Shearly API: get_dynamic_price()", color: COLORS.accent, active: true },
              { id: 5, title: "Preis mitteilen", desc: "\"Das kostet ca. X € und dauert Y Stunden\"", color: COLORS.primary, active: true },
              { id: 6, title: "Mitarbeiter & Termin vorschlagen", desc: "→ Shearly API: check_availability()", color: COLORS.accent, active: true },
              { id: 7, title: "Buchung bestätigen", desc: "→ Shearly API: create_booking() + SMS", color: COLORS.success, active: true },
              { id: 8, title: "Verabschiedung", desc: "Zusammenfassung + Auf Wiederhören", color: COLORS.success, active: true },
            ].map((step, i) => (
              <div key={step.id}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 10,
                  border: `1px solid ${step.color}30`, background: `${step.color}06`, marginBottom: 0
                }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: "50%", background: step.color, color: "white",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0
                  }}>
                    {step.id}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{step.title}</div>
                    <div style={{ fontSize: 11, color: COLORS.textLight }}>{step.desc}</div>
                  </div>
                  <Badge text={step.active ? "Aktiv" : "Aus"} color={step.active ? COLORS.success : COLORS.textLight} />
                </div>
                {i < 7 && <div style={{ marginLeft: 22, borderLeft: `2px solid ${COLORS.border}`, height: 8 }} />}
              </div>
            ))}
          </Card>

          <Card>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Eskalationsregeln</h3>
            {[
              { trigger: "Kunde sagt \"Mitarbeiter\" / \"Mensch\"", action: "Sofort weiterleiten", priority: "Hoch" },
              { trigger: "KI versteht Anliegen nach 3 Versuchen nicht", action: "Weiterleiten + Entschuldigung", priority: "Hoch" },
              { trigger: "Beschwerde / unzufriedener Ton erkannt", action: "Weiterleiten an Manager", priority: "Hoch" },
              { trigger: "Komplexe Farbberatung gewünscht", action: "Weiterleiten an Colorist", priority: "Mittel" },
              { trigger: "Preisverhandlung / Sonderwünsche", action: "Weiterleiten", priority: "Mittel" },
              { trigger: "Max. Gesprächsdauer erreicht", action: "Zusammenfassen + Weiterleiten", priority: "Niedrig" },
            ].map((rule, i) => (
              <div key={i} style={{ padding: "10px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontWeight: 600 }}>{rule.trigger}</span>
                  <Badge text={rule.priority} color={rule.priority === "Hoch" ? COLORS.danger : rule.priority === "Mittel" ? COLORS.warning : COLORS.info} />
                </div>
                <div style={{ color: COLORS.textLight, marginTop: 2 }}>→ {rule.action}</div>
              </div>
            ))}

            <div style={{ marginTop: 20 }}>
              <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Kosten-Übersicht</h3>
              <div style={{ padding: 14, background: "var(--cardHover)", borderRadius: 10, fontSize: 13, lineHeight: 2 }}>
                <div><strong>Retell AI:</strong> 0,07 $/Min. (flat, all-inclusive)</div>
                <div><strong>Ø Anrufdauer:</strong> 1:30 Min.</div>
                <div><strong>Ø Kosten/Anruf:</strong> ~0,11 €</div>
                <div><strong>Bei 20 Anrufen/Tag:</strong> ~2,20 €/Tag = ~66 €/Monat</div>
                <div style={{ marginTop: 8, fontWeight: 700, color: COLORS.success }}>
                  vs. Rezeptionistin: ~2.500 €/Monat → <strong>97% Ersparnis</strong>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============================================================
// PAGE: Messages / SMS
// ============================================================
export const MessagesPage = ({ customers, onGoToSubscription }) => {
  /* SMS_TRIAL_LOCK_v1 -- Fenster "erst nach Kauf", wenn ein Trial-Salon eine SMS-Automatik einschalten will */
  const [trialLockModal, setTrialLockModal] = useState(false);
  // SMS_LIVE_v1 -- ersetzt Demo-Daten (Salon Schönheit, Sarah, Klaus) durch echte
  // twilio_sms_logs aus /api/salon/sms-stats.
  const [smsData, setSmsData] = useState(null);
  const [smsLoading, setSmsLoading] = useState(true);
  useEffect(() => {
    api.request("/salon/sms-stats").then(r => {
      if (r && r.ok) setSmsData(r.data || {});
      setSmsLoading(false);
    }).catch(() => setSmsLoading(false));
  }, []);

  // SMS-Typ aus Body-Excerpt grob klassifizieren (Backend speichert keinen type)
  const inferType = (preview) => {
    const p = (preview || "").toLowerCase();
    if (/erinnerung|reminder|morgen|heute um|bis gleich/.test(p)) return "reminder";
    if (/geburtstag|happy|🎂|bday/.test(p)) return "birthday";
    if (/buchung|gebucht|bestätigung|reserviert/.test(p)) return "confirm";
    if (/bewertung|google|review|sterne/.test(p)) return "review";
    return "info";
  };
  const typeConfig = {
    reminder: { label: "Erinnerung", color: COLORS.info, icon: Bell },
    birthday: { label: "Geburtstag", color: COLORS.accent, icon: Star },
    confirm:  { label: "Bestätigung", color: COLORS.success, icon: CheckCircle },
    review:   { label: "Bewertung", color: COLORS.warning, icon: Star },
    info:     { label: "SMS", color: COLORS.primary, icon: Send },
  };
  const statusColor = (s) => {
    const x = (s || "").toLowerCase();
    if (x === "delivered" || x === "sent") return COLORS.success;
    if (x === "queued" || x === "sending" || x === "accepted") return COLORS.info;
    if (x === "failed" || x === "undelivered") return COLORS.danger;
    return COLORS.textLight;
  };
  const statusLabel = (s) => {
    const x = (s || "").toLowerCase();
    if (x === "delivered") return "Zugestellt";
    if (x === "sent") return "Gesendet";
    if (x === "queued" || x === "accepted") return "In Warteschlange";
    if (x === "sending") return "Sende...";
    if (x === "failed") return "Fehlgeschlagen";
    if (x === "undelivered") return "Nicht zugestellt";
    return s || "—";
  };
  const fmtAt = (at) => {
    if (!at) return "";
    try {
      const d = new Date(at);
      return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch (e) { return at; }
  };

  const recent = (smsData && smsData.recent) || [];
  const monthCount = smsData && smsData.month && smsData.month.count;
  const todayCount = smsData && smsData.today && smsData.today.count;
  const last30Count = smsData && smsData.last30 && smsData.last30.count; /* SMS_COST_HIDE_v1 */

  return (
    <div>
      <Header title="SMS & Nachrichten" subtitle="Automatische Erinnerungen & Marketing" />

      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
        <StatCard icon={Send} label="SMS gesendet (Monat)" value={smsLoading ? "…" : String(monthCount || 0)} color={COLORS.primary} />
        <StatCard icon={Bell} label="SMS gesendet (heute)" value={smsLoading ? "…" : String(todayCount || 0)} color={COLORS.info} />
        <StatCard icon={MessageSquare} label="SMS letzte 30 Tage" value={smsLoading ? "…" : String(last30Count || 0)} color={COLORS.accent} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "2fr 1fr", gap: 16 }}>
        <Card>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Letzte Nachrichten</h3>
          {smsLoading ? (
            <div style={{ padding: 24, textAlign: "center", color: COLORS.textLight, fontSize: 13 }}>Wird geladen…</div>
          ) : recent.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: COLORS.textLight, fontSize: 13, lineHeight: 1.5 }}>
              Noch keine SMS gesendet.<br/>
              <span style={{ fontSize: 12 }}>Sobald eine Buchungsbestätigung, Erinnerung oder Geburtstags-SMS rausgeht, erscheint sie hier.</span>
            </div>
          ) : recent.map((msg, idx) => {
            const t = inferType(msg.preview);
            const cfg = typeConfig[t];
            const Icon = cfg.icon;
            return (
              <div key={idx} style={{ padding: "12px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: `${cfg.color}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={16} color={cfg.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: COLORS.text }}>+••• {msg.to || "—"}</span>
                    <span style={{ fontSize: 11, color: COLORS.textLight, whiteSpace: "nowrap" }}>{fmtAt(msg.at)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {msg.preview || <i>(kein Inhalt gespeichert)</i>}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Badge text={cfg.label} color={cfg.color} />
                    <Badge text={statusLabel(msg.status)} color={statusColor(msg.status)} />
                  </div>
                </div>
              </div>
            );
          })}
        </Card>

        <Card>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>SMS-Automatisierung</h3>
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 14px" }}>Wähle aus, welche SMS automatisch an deine Kunden gehen. Änderungen werden sofort gespeichert.</p>
          {/* SMS_QUOTA_v1 -- Verbrauchsbalken, nur wenn das Kontingent fuer den Salon aktiv ist */}
          {smsData && smsData.quota && smsData.quota.enabled && (() => {
            const q = smsData.quota;
            const pct = q.included > 0 ? Math.min(100, Math.round(q.used / q.included * 1000) / 10) : 100;
            const barColor = pct >= 100 ? COLORS.danger : pct >= 80 ? "#F59E0B" : COLORS.success;
            return (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>
                  <span>SMS-Kontingent diesen Monat</span>
                  <span>{q.used} / {q.included} ({pct}%)</span>
                </div>
                <div style={{ height: 8, background: "var(--cardHover)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: Math.min(100, pct) + "%", background: barColor, transition: "width 0.3s" }} />
                </div>
                <div style={{ fontSize: 10, color: (q.overage > 0 && q.billing_active) ? COLORS.danger : COLORS.textLight, marginTop: 4 }}>
                  {/* SMS_QUOTA_BAR_NOW_v1 -- Kostenhinweis nur bei aktiver Abrechnung + Ueberschreitung */}
                  {q.overage > 0 && q.billing_active
                    ? `${q.overage} SMS über dem Kontingent — jede weitere ${q.overage_price_eur.toFixed(2)} € netto, automatische Abrechnung am Monatsanfang.`
                    : (q.overage > 0 ? `Kontingent diesen Monat ausgeschöpft.` : `Noch ${q.remaining} Inklusiv-SMS diesen Monat.`)}
                </div>
              </div>
            );
          })()}
          {(() => {
            const [smsSettings, setSmsSettings] = useState(null);
            const [smsMsg, setSmsMsg] = useState("");
            useEffect(() => {
              api.request("/salon/sms-settings").then(res => {
                if (res.ok) setSmsSettings(res.data);
                else setSmsSettings({ sms_booking_confirm: true, sms_reminder_24h: true, sms_reminder_2h: true, sms_manual_booking: false, sms_review_request: true });
              });
            }, []);
            const toggleSms = async (field) => {
              const turningOn = !smsSettings[field];
              /* SMS_TRIAL_LOCK_v1 -- im Trial: Einschalten blockiert, Fenster mit Hinweis auf den Kauf */
              if (turningOn && smsSettings.trial_locked && field !== "prefer_email_notifications" && field !== "booking_email_required") { setTrialLockModal(true); return; }
              const updated = { ...smsSettings, [field]: turningOn };
              setSmsSettings(updated);
              setSmsMsg("");
              const res = await api.request("/salon/sms-settings", { method: "PUT", body: JSON.stringify({ [field]: updated[field] }) });
              if (res.ok) setSmsMsg("Gespeichert");
              else if (res.status === 403 && res.data && res.data.code === "TRIAL_SMS_LOCKED") { setSmsSettings({ ...smsSettings, trial_locked: true }); setTrialLockModal(true); }
              else setSmsMsg("Fehler beim Speichern");
              setTimeout(() => setSmsMsg(""), 2000);
            };
            const smsTypes = [
              // SMS_OWNER_TRIM_v1 -- Inhaber sieht nur noch Bestätigung + 24h-Erinnerung.
              // Manuelle Buchung, 2h-Erinnerung, Google-Bewertung steuert nur der Admin (Toby) pro Salon.
              { key: "sms_booking_confirm", label: "Buchungsbestätigung", desc: "SMS direkt nach einer Online-Buchung" },
              { key: "sms_reminder_24h", label: "24h-Erinnerung", desc: "Erinnerung am Vortag des Termins" },
              // EMAIL_FIRST_v1 -- Kanal-Wahl: E-Mail zuerst, SMS nur ohne E-Mail-Adresse
              { key: "prefer_email_notifications", label: "Bevorzugt per E-Mail senden", desc: "Bestätigung und Erinnerung gehen per E-Mail, wenn beim Kunden eine E-Mail-Adresse hinterlegt ist. SMS nur noch für Kunden ohne E-Mail-Adresse — das spart SMS-Kosten." },
              // BOOKING_EMAIL_REQUIRED_v1 (Toby 30.09.2026) -- E-Mail Pflichtfeld auf der Buchungsseite
              { key: "booking_email_required", label: "E-Mail bei der Online-Buchung ist Pflicht", desc: "Kunden müssen beim Online-Buchen zusätzlich zu Name und Telefon ihre E-Mail-Adresse angeben. Sie wird in der Kundenkarte gespeichert, wenn dort noch keine steht. Ohne E-Mail ist online kein Termin möglich." },
            ];
            if (!smsSettings) return <div style={{ fontSize: 13, color: "#6b7280" }}>Lädt…</div>;
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {smsTypes.map(t => (
                  <div key={t.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: smsSettings[t.key] ? "rgba(34,197,94,0.10)" : "var(--cardHover)", borderRadius: 8, border: "1px solid " + (smsSettings[t.key] ? "rgba(34,197,94,0.35)" : "var(--border)"), cursor: "pointer" }} onClick={() => toggleSms(t.key)}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</div>
                      <div style={{ fontSize: 11, color: "var(--textLight)" }}>{t.desc}</div>
                    </div>
                    <div style={{ width: 40, height: 22, borderRadius: 11, background: smsSettings[t.key] ? "#22c55e" : "#6B7280", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: smsSettings[t.key] ? 20 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                    </div>
                  </div>
                ))}
                {smsMsg && <div style={{ marginTop: 10, fontSize: 12, color: smsMsg === "Gespeichert" ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{smsMsg}</div>}
                {/* SMS_TRIAL_LOCK_v1 -- Hinweis unter den Schaltern, solange der Salon im Trial ist */}
                {smsSettings.trial_locked && (
                  <div style={{ marginTop: 8, fontSize: 12, color: COLORS.textLight, lineHeight: 1.5, padding: "8px 10px", background: "var(--cardHover)", borderRadius: 8 }}>
                    🔒 SMS-Versand ist in der Testphase noch nicht verfügbar. {onGoToSubscription ? <a href="#" onClick={e => { e.preventDefault(); onGoToSubscription(); }} style={{ color: COLORS.primary, fontWeight: 600 }}>Tarif wählen</a> : "Nach dem Kauf eines Tarifs schaltbar."}
                  </div>
                )}
              </div>
            );
          })()}
        </Card>
      </div>
      {/* SMS_TRIAL_LOCK_v1 -- Fenster beim Versuch, im Trial eine SMS-Automatik einzuschalten */}
      {trialLockModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.55)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => setTrialLockModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: COLORS.card, borderRadius: 14, padding: 22, width: "100%", maxWidth: 440, boxShadow: "0 12px 40px rgba(0,0,0,0.32)" }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: COLORS.text, marginBottom: 8 }}>🔒 SMS-Versand ab dem gebuchten Tarif</div>
            <p style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.6, margin: "0 0 14px" }}>
              In der Testphase bleibt der SMS-Versand ausgeschaltet. Sobald du einen Tarif gebucht hast, kannst du Buchungsbestätigung und 24h-Erinnerung per SMS einschalten – <b>400 SMS pro Monat sind inklusive</b>.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              {onGoToSubscription && (
                <button onClick={() => { setTrialLockModal(false); onGoToSubscription(); }}
                  style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: COLORS.primary, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Tarif wählen</button>
              )}
              <button onClick={() => setTrialLockModal(false)}
                style={{ flex: 1, padding: "10px", borderRadius: 8, border: "1px solid " + COLORS.border, background: "transparent", color: COLORS.text, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Später</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin: Salon-Verwaltung
// RETELL_PROVISION_v1 — Self-Service-Setup-Card im Admin-Salon-Modal
// Zeigt aktuellen Stand (Agent vorhanden? Phone-Nummer? Aktiviert?) + Provision-Button.
export const RetellSetupCard = ({ salonId, salonName }) => {
  const [status, setStatus] = React.useState(null);
  const [provisioning, setProvisioning] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  const [msgType, setMsgType] = React.useState(""); // 'ok' | 'err'

  const load = () => {
    setMsg("");
    fetch(`/api/admin/salons/${salonId}/retell-status`, {
      headers: { "Authorization": "Bearer " + sessionStorage.getItem("sp_token") },
    }).then(r => r.json()).then(d => setStatus(d)).catch(() => setStatus({ error: "Status nicht ladbar" }));
  };
  React.useEffect(() => { if (salonId) load(); }, [salonId]);

  const provision = async () => {
    if (!confirm(`KI-Telefonie-Agent für "${salonName || 'diesen Salon'}" provisionieren?\n\n` +
                 `Was passiert:\n` +
                 `• Neuer Retell-Agent mit salon-spezifischem Prompt wird erstellt\n` +
                 `• Begrüßungs-Text mit Salonnamen + aktuellem Datum\n` +
                 `• Services + Mitarbeiter werden eingelesen\n\n` +
                 `Kosten: 0 € (Phone-Nummer wird NICHT gekauft).\n` +
                 `Danach: Nummer kaufen + im Dashboard eintragen.`)) return;
    setProvisioning(true);
    setMsg("");
    try {
      const r = await fetch(`/api/admin/salons/${salonId}/provision-retell`, {
        method: "POST",
        headers: {
          "Authorization": "Bearer " + sessionStorage.getItem("sp_token"),
          "Content-Type": "application/json",
        },
        body: "{}",
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setMsgType("ok");
        setMsg(d.message || "Agent provisioniert");
        load();
      } else {
        setMsgType("err");
        setMsg((d.detail || d.error || "Fehler") + (r.status === 502 ? " (Retell-API)" : ""));
      }
    } catch (e) {
      setMsgType("err");
      setMsg("Netzwerk-Fehler: " + (e.message || "?"));
    }
    setProvisioning(false);
  };

  if (!status) return (
    <div style={{ marginLeft: 26, fontSize: 12, color: COLORS.textLight, padding: "6px 0" }}>
      Lade KI-Telefonie-Status…
    </div>
  );
  if (status.error) return null; // still verstecken bei API-Fehler — Toby sieht's im normalen Modal-Save

  const hasAgent = !!status.has_agent;
  const hasPhone = !!status.has_phone;
  const live = hasAgent && hasPhone && status.enabled;

  const StatusBadge = ({ ok, warn, label }) => (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600,
      background: ok ? "rgba(16,185,129,0.12)" : warn ? "rgba(245,158,11,0.12)" : "rgba(156,163,175,0.12)",
      color: ok ? "#059669" : warn ? "#B45309" : "#6B7280",
    }}>
      {ok ? "✓" : warn ? "○" : "✗"} {label}
    </span>
  );

  return (
    <div style={{
      marginLeft: 26, marginTop: 4,
      background: "var(--cardHover)", border: "1px solid var(--border)",
      borderRadius: 8, padding: "10px 14px",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>
          🤖 Retell-Agent-Setup
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <StatusBadge ok={hasAgent} label={hasAgent ? "Agent bereit" : "Kein Agent"} />
          <StatusBadge ok={hasPhone} warn={hasAgent && !hasPhone} label={hasPhone ? "Nummer da" : "Nummer fehlt"} />
          <StatusBadge ok={live} warn={hasAgent && !live} label={live ? "Live" : "Nicht live"} />
        </div>
      </div>

      <div style={{ fontSize: 11.5, color: COLORS.textLight, lineHeight: 1.55, marginBottom: 8 }}>
        {!hasAgent && (
          <>Provisioniere einen Retell-Agent mit salon-spezifischem Prompt
            ({status.service_count || 0} Services). Kostet 0 €. Erst beim Phone-Number-Kauf entstehen Kosten.</>
        )}
        {hasAgent && !hasPhone && (
          <>Agent <code style={{ fontSize: 10 }}>{(status.agent_id || "").slice(0, 16)}…</code> ist bereit.
            <b> Nächster Schritt:</b> Telefonnummer in der Retell-Console kaufen + im Salon-Profil eintragen.
            Re-Provisionieren überschreibt den Agent (Prompt wird neu aus aktuellen Daten gebaut).</>
        )}
        {hasAgent && hasPhone && (
          <>Live unter <code style={{ fontSize: 11 }}>{status.phone}</code>. Voice: {status.voice_id || "-"}, Locale: {status.locale || "-"}.
            Re-Provisionieren regeneriert den Prompt aus aktuellen Salon-Daten.</>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          onClick={provision}
          disabled={provisioning}
          style={{
            padding: "7px 12px", borderRadius: 6, border: "1px solid " + COLORS.primary,
            background: COLORS.primary, color: "#fff",
            fontSize: 12, fontWeight: 700, cursor: provisioning ? "wait" : "pointer",
            opacity: provisioning ? 0.6 : 1,
          }}>
          {provisioning ? "Provisioniere…" : (hasAgent ? "🔄 Re-Provisionieren" : "📞 Agent provisionieren")}
        </button>
        {status.activated_at && (
          <span style={{ fontSize: 10, color: COLORS.textLight, alignSelf: "center" }}>
            Zuletzt aktiviert: {new Date(status.activated_at).toLocaleString("de-DE")}
          </span>
        )}
      </div>

      {msg && (
        <div style={{
          marginTop: 8, padding: "6px 10px", borderRadius: 6, fontSize: 11.5,
          background: msgType === "ok" ? "rgba(16,185,129,0.10)" : "rgba(239,68,68,0.10)",
          color: msgType === "ok" ? "#059669" : "#991B1B",
        }}>
          {msgType === "ok" ? "✓ " : "✗ "}{msg}
        </div>
      )}
    </div>
  );
};